/**
 * What a character profile is, as one record.
 *
 * This is the save format. A field renamed here is a migration, not a rename, and the same is true
 * of every field it reaches into: `InventoryState` says so in its own header for exactly this
 * reason.
 *
 * One integer version covers the whole record. The inventory keeps its own
 * `INVENTORY_SCHEMA_VERSION` as its validation gate, but a player has one save with one version,
 * and `SAVE_SCHEMA_VERSION` is that number. A section that changes shape is a step in the one
 * chain, so "is this save readable" stays a question with a single answer.
 *
 * What is not here is as deliberate as what is. Unlocked zones are state no system owns yet, and
 * each such thing arrives as a new section with the issue that builds it. An empty field reserved
 * today is a field the system that eventually needs it has to migrate away from.
 *
 * `progress` is what that looks like when it happens: version 2 is progression arriving, as a
 * section with a step in the chain behind it, rather than as a field that had been sitting empty.
 * `settings` is version 3, arriving the same way.
 */

import { BASELINE_STATS } from '@/sim/combat/statContract';
import { NEW_PROGRESS } from '@/sim/progression/progressState';
import { BASE_CARRY_KG, STASH_CAPACITY_KG } from '@/sim/inventory/carryWeight';
import { createInventory } from '@/sim/inventory/inventoryContract';
import type { StatBlock } from '@/sim/combat/statContract';
import type { SaveRecord } from '@/save/migrationContract';
import type { InventoryState } from '@/sim/inventory/inventoryContract';
import type { ProgressState } from '@/sim/progression/progressState';

/**
 * The record's version, written on every save and checked before anything else is read.
 * `readProfile.ts` owns what happens when it does not match, `migrationChain.ts` how it catches up.
 */
export const SAVE_SCHEMA_VERSION = 4;

/**
 * Every knob the player has moved, by knob id.
 *
 * An open record rather than a field per setting, and empty means every knob is on its default. A
 * typed field per knob would put the settings registry in the save format, so adding a knob would
 * be a migration and removing one would strand a field forever.
 *
 * Untyped for the same reason `SaveRecord` is: this build's registry decides what a key means, and
 * a key it does not know is kept rather than dropped. `ui/settings/settingValues.ts` owns the rules
 * for turning one of these into a value, and it never refuses.
 */
export type StoredSettings = Readonly<Record<string, number | string>>;

export interface SaveProfile {
  readonly schemaVersion: number;
  /** Stable for the life of the profile, and the key the record is stored under. */
  readonly profileId: string;
  /** Rolled, never typed, and sanitized again on the way in from disk. */
  readonly name: string;
  readonly stats: StatBlock;
  /**
   * Experience and perk ranks. The level is not in here because it is `levelForXp(xp)`, and a
   * stored level is a second number that can disagree with the first.
   */
  readonly progress: ProgressState;
  readonly inventory: InventoryState;
  /** Knobs the player has moved. Empty on a character nobody has been into the settings for. */
  readonly settings: StoredSettings;
  readonly createdAtMs: number;
  readonly savedAtMs: number;
}

/** Enough to draw a row in a character picker without loading a whole profile's worth of gear. */
export interface ProfileSummary {
  readonly profileId: string;
  readonly name: string;
  readonly savedAtMs: number;
}

export function summarize(profile: SaveProfile): ProfileSummary {
  return { profileId: profile.profileId, name: profile.name, savedAtMs: profile.savedAtMs };
}

/**
 * A character nobody has played yet.
 *
 * Baseline stats and an empty pair of containers, sized from the same constants the rest of the
 * game reads, so a new profile and a loaded one agree on what a backpack holds.
 */
export function newProfile(profileId: string, name: string, nowMs: number): SaveProfile {
  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    profileId,
    name,
    stats: BASELINE_STATS,
    progress: NEW_PROGRESS,
    inventory: createInventory(BASE_CARRY_KG, STASH_CAPACITY_KG),
    settings: {},
    createdAtMs: nowMs,
    savedAtMs: nowMs,
  };
}

/**
 * The profile as a record for storage.
 *
 * A `SaveProfile` is already plain data, so this is a copy and a widening rather than a conversion.
 * It exists so the write path names the moment a typed profile becomes an untyped record, which is
 * the moment the migration chain will one day have to reason about.
 */
export function toSaveRecord(profile: SaveProfile): SaveRecord {
  return { ...profile };
}
